import { ReactElement, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  createStyles,
  makeStyles,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Box,
  Hidden,
  Theme,
  useMediaQuery
} from '@material-ui/core'
import { AccountCircle, ExitToApp } from '@material-ui/icons'
import Urls from 'ustaxes/data/urls'
import RefundTracker from './RefundTracker'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    appBar: {
      backgroundColor: '#FFFFFF',
      borderBottom: '1px solid rgba(0,0,0,0.08)',
      marginTop: 4, // space for patriotic bar
      zIndex: theme.zIndex.drawer + 1
    },
    appBarDark: {
      backgroundColor: '#1A1E3A',
      borderBottom: '1px solid rgba(255,255,255,0.08)',
      marginTop: 4,
      zIndex: theme.zIndex.drawer + 1
    },
    toolbar: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: theme.spacing(0, 3),
      minHeight: 64
    },
    logoSection: {
      display: 'flex',
      alignItems: 'center',
      textDecoration: 'none',
      color: 'inherit',
      gap: theme.spacing(1)
    },
    flagEmoji: {
      fontSize: 28,
      lineHeight: 1
    },
    logoText: {
      fontWeight: 800,
      fontSize: '1.35rem',
      letterSpacing: '-0.02em',
      color: '#1A237E'
    },
    logoTextDark: {
      fontWeight: 800,
      fontSize: '1.35rem',
      letterSpacing: '-0.02em',
      color: '#E8EAF6'
    },
    tagline: {
      fontSize: '0.7rem',
      fontWeight: 500,
      color: theme.palette.text.secondary,
      letterSpacing: '0.05em',
      textTransform: 'uppercase',
      marginLeft: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        display: 'none'
      }
    },
    centerSection: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2)
    },
    rightSection: {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(1.5)
    },
    avatar: {
      width: 34,
      height: 34,
      backgroundColor: theme.palette.primary.main,
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: 700
    },
    signInBtn: {
      borderRadius: 20,
      padding: '6px 20px',
      fontSize: '0.85rem'
    }
  })
)

interface HeaderProps {
  user?: {
    displayName: string | null
    email: string | null
    photoURL: string | null
  } | null
  onSignOut?: () => void
}

const Header = ({ user, onSignOut }: HeaderProps): ReactElement => {
  const classes = useStyles()
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)')
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
  }

  const handleSignOut = () => {
    handleMenuClose()
    onSignOut?.()
  }

  const getInitials = (name: string | null | undefined): string => {
    if (!name) return 'U'
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  return (
    <AppBar
      position="fixed"
      className={prefersDarkMode ? classes.appBarDark : classes.appBar}
      elevation={0}
    >
      <Toolbar className={classes.toolbar}>
        {/* Logo */}
        <Link to={Urls.usTaxes.start} className={classes.logoSection}>
          <span className={classes.flagEmoji} role="img" aria-label="US flag">
            {'\u{1F1FA}\u{1F1F8}'}
          </span>
          <Typography
            className={
              prefersDarkMode ? classes.logoTextDark : classes.logoText
            }
          >
            Ameritax
          </Typography>
          <Typography className={classes.tagline}>
            AI-Assisted Tax Filing
          </Typography>
        </Link>

        {/* Center - refund tracker */}
        <Hidden smDown>
          <Box className={classes.centerSection}>
            <RefundTracker />
          </Box>
        </Hidden>

        {/* Right section */}
        <Box className={classes.rightSection}>
          {user ? (
            <>
              <Avatar
                className={classes.avatar}
                src={user.photoURL ?? undefined}
                onClick={handleMenuOpen}
              >
                {!user.photoURL && getInitials(user.displayName)}
              </Avatar>
              <Hidden smDown>
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {user.displayName || user.email}
                </Typography>
              </Hidden>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                getContentAnchorEl={null}
              >
                <MenuItem disabled>
                  <Typography variant="body2" color="textSecondary">
                    {user.email}
                  </Typography>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleMenuClose()
                    navigate(Urls.settings)
                  }}
                >
                  <AccountCircle
                    style={{ marginRight: 8 }}
                    fontSize="small"
                  />
                  Settings
                </MenuItem>
                <MenuItem onClick={handleSignOut}>
                  <ExitToApp style={{ marginRight: 8 }} fontSize="small" />
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Button
              variant="contained"
              color="primary"
              size="small"
              className={classes.signInBtn}
              component={Link}
              to={Urls.login}
            >
              Sign In
            </Button>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  )
}

export default Header
